import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React from "react";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import {
  useGetCategoriesQuery,
  useGetManufacturersQuery,
  useGetProductByIdMutation,
  useGetProductsMutation,
} from "../redux/reducer/features/Product/productSlice";
import { Product } from "../models/features/Product/Product";
import { useDispatch, useSelector } from "react-redux";
import { AppFilter, AppFilterItem } from "../models/app/AppModels";
import ServerAppGrid from "../compenents/app/ServerAppGrid";
import ProductDetail from "../compenents/Integration/DmsSetup/Popup/ProductDetail";
import { saveFilters } from "../redux/reducer/features/Product/productSlice";
import { AppState } from "../redux/reducer/rootReducer";
import { AppGridId } from "../models/app/AppGridModels";
import { GetAppGridColumns } from "../services/utilities/utility";

export interface HomeProps {}

export const Products: React.FunctionComponent<HomeProps> = () => {
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.Catalogue_Products
    )
  );
  const { data, isLoading, status } = useGetCategoriesQuery();
  const manufacturers = useGetManufacturersQuery();
  const { filters } = useSelector((state: AppState) => state.productReducer);
  const [paging, setPaging] = React.useState<AppFilter>(filters);
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = React.useState<string>();
  const [selectedManufacturer, setSelectedManufacturer] =
    React.useState<string>();
  const [sectionFlag, setSectionFlag] = React.useState(false);
  const [draftOnlyFlag, setDraftOnlyFlag] = React.useState(false);
  const [openDetail, setOpenDetail] = React.useState(false);
  const [productsData, productsDataResult] = useGetProductsMutation();
  const [products, setProducts] = React.useState<any[]>([]);
  const [selectedRow, setSelectedRow] = React.useState<Product>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [productByIdMutation, productByIdMutationResult] =
    useGetProductByIdMutation();

  const categoryDefaultProps = {
    options:
      data && data.entityList
        ? data.entityList.filter((n) => !n.isSubCategory)
        : [],
    getOptionLabel: (option: any) => option.title,
  };

  const manufacturerDefaultProps = {
    options:
      manufacturers.data && manufacturers.data.entityList
        ? manufacturers.data.entityList
        : [],
    getOptionLabel: (option: any) => option.title,
  };
  const open = Boolean(anchorEl);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: Product
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(item);
  };
  const handleClose = (params: any) => {
    setAnchorEl(null);
  };
  const handleCloseDetail = () => {
    setOpenDetail(false);
  };
  const handleGetProductDetail = () => {
    setOpenDetail(true);
    handleClose(null);
    productByIdMutation({
      data: { id: selectedRow?.id },
    }).then((res: any) => {
      if (res.data.status === 0) {
        setSelectedProduct(res.data.entity);
      } else {
        setOpenDetail(false);
      }
    });
  };

  const handleDraftsOrSectionsOnly = (draftRequest: boolean) => {
    if (draftRequest) {
      setDraftOnlyFlag(!draftOnlyFlag);
    } else {
      setSectionFlag(!sectionFlag);
    }
    setPaging({
      items: [],
      pageSize: 10,
      pageIndex: 0,
      quickFilterValues: [],
      totalRecords: 0,
      categoryIds: selectedCategory ? [selectedCategory] : [],
      manufacturerIds: selectedManufacturer ? [selectedManufacturer] : [],
    });
  };

  const fetchData = () => {
    productsData({
      paging: {
        ...paging,
        sectionOnly: sectionFlag,
        draftOnly: draftOnlyFlag,
      },
    });
  };

  React.useEffect(() => {
    setPaging({
      ...paging,
      categoryIds: selectedCategory ? [selectedCategory] : [],
      manufacturerIds: selectedManufacturer ? [selectedManufacturer] : [],
    });
  }, [selectedCategory, selectedManufacturer]);

  React.useEffect(() => {
    if (productsDataResult.data) {
      setProducts(productsDataResult.data.entityList);
      setProductsCount(productsDataResult.data.totalRecords);
    }
  }, [productsDataResult]);

  React.useEffect(() => {
    fetchData();
    dispatch(saveFilters(paging));
  }, [paging]);

  const columns: GridColDef[] = [
    {
      field: "manufacturer",
      headerName: "Manufacturer",
      flex: 1,
      filterable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.productManufacturer.title,
    },
    {
      field: "modelNumber",
      headerName: "Model Number",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={"flex"} alignItems={"center"} gap={2}>
            {params.row?.images?.length > 0 ? (
              <Box
                style={{
                  width: "50px",
                  height: "50px",
                  backgroundColor: "white",
                }}
              >
                <img
                  style={{ maxWidth: "100%", maxHeight: "100%" }}
                  alt=""
                  src={params.row?.images[0].absoluteUrl}
                ></img>
              </Box>
            ) : null}
            <Typography sx={{ cursor: "pointer" }} color="primary">
              {params.row.modelNumber}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
    },
    {
      field: "shortTitle",
      headerName: "Short Title",
      flex: 1,
    },
    {
      field: "categoryTitle",
      headerName: "Category",
      flex: 1,
      filterable: false,
      valueGetter: (params: GridValueGetterParams) => params.row.category.title,
    },
    {
      field: "sku",
      headerName: "SKU",
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <IconButton
              id="basic-button"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={(e) => {
                handleClick(e, params.row);
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleGetProductDetail();
                }}
              >
                <ListItemIcon>
                  <InfoIcon />
                </ListItemIcon>
                <ListItemText>Details</ListItemText>
              </MenuItem>
              <MenuItem component={Link} to={"/EditProduct/" + selectedRow?.id}>
                <ListItemIcon>
                  <EditIcon />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
              {sectionFlag && (
                <MenuItem
                  onClick={() => {
                    window.open(`/ProductDetailView/${selectedRow?.id}`);
                  }}
                >
                  <ListItemIcon>
                    <EditIcon />
                  </ListItemIcon>
                  <ListItemText>Public View</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Container maxWidth={false}>
        <Card>
          <CardContent>
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              display="flex"
            >
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold" }}
                    gutterBottom
                  >
                    Products
                  </Typography>
                  <Box display="flex">
                    <FormControlLabel
                      control={
                        <Switch
                          name="draftOnly"
                          value={draftOnlyFlag}
                          onChange={() => handleDraftsOrSectionsOnly(true)}
                        />
                      }
                      label="Drafts only"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="sectionOnly"
                          value={sectionFlag}
                          onChange={() => handleDraftsOrSectionsOnly(false)}
                        />
                      }
                      label="Parts Sections only"
                    />
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ mr: 2 }}
                      component={Link}
                      to="/ProductsGallery"
                    >
                      Gallery View
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      component={Link}
                      to="/AddProduct"
                      startIcon={<AddIcon />}
                    >
                      New Product
                    </Button>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  {...manufacturerDefaultProps}
                  id="manufacturersList"
                  clearOnEscape
                  loading={isLoading}
                  onChange={(e, value) => {
                    setSelectedManufacturer(value?.id ?? "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manufacturer"
                      variant="standard"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  {...categoryDefaultProps}
                  id="categoriesList"
                  loading={isLoading}
                  clearOnEscape
                  onChange={(e, value) => {
                    setSelectedCategory(value?.id ?? "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      variant="standard"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
          <Box sx={{ width: "100%" }}>
            <ServerAppGrid
              gridId={AppGridId.Catalogue_Products}
              data={products}
              columns={GetAppGridColumns(
                columns,
                gridSetting?.columnVisibilityModel
              )}
              isLoading={productsDataResult.isLoading}
              paging={paging}
              setPaging={setPaging}
              totalRecords={productsCount}
              height={"70vh"}
              hideCheckBoxes={true}
            />
          </Box>
        </Card>
      </Container>
      <ProductDetail
        isOpen={openDetail}
        handleClose={handleCloseDetail}
        productData={selectedProduct}
        Loading={productByIdMutationResult.isLoading}
        showSyncedProducts={true}
      />
    </>
  );
};
